import { ExportStatusValue } from 'types/result';
import { Spinner, Tooltip, TooltipContent, TooltipTrigger } from 'components';
import { Circle, CircleAlert, CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExportStatusIconProps {
  status?: ExportStatusValue;
  animate?: boolean;
}

export const ExportStatusIcon = ({ status, animate = true }: ExportStatusIconProps) => {
  const { t } = useTranslation();

  const getIcon = () => {
    if (status === undefined || status === ExportStatusValue.NoStatus) {
      return <Circle className="h-4 w-4" />;
    }

    if (
      status === ExportStatusValue.Pending ||
      status === ExportStatusValue.Progress ||
      status === ExportStatusValue.Retry
    ) {
      return <Spinner size="xxs" animate={animate} />;
    }

    if (status === ExportStatusValue.Success) {
      return <CircleCheck className="text-green-500 h-4 w-4" />;
    }

    if (status === ExportStatusValue.Failure || status === ExportStatusValue.Error) {
      return <CircleAlert className="text-red-500 h-4 w-4" />;
    }

    return null;
  };

  const getTooltipKey = () => {
    if (status === undefined || status === ExportStatusValue.NoStatus) {
      return 'not_exported';
    }

    if (
      status === ExportStatusValue.Pending ||
      status === ExportStatusValue.Progress ||
      status === ExportStatusValue.Retry
    ) {
      return 'processing';
    }

    if (status === ExportStatusValue.Success) {
      return 'success';
    }

    if (status === ExportStatusValue.Failure || status === ExportStatusValue.Error) {
      return 'failed';
    }

    return null;
  };

  const icon = getIcon();
  const tooltipKey = getTooltipKey();

  if (!icon) {
    return null;
  }

  if (!tooltipKey) {
    return icon;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{icon}</span>
      </TooltipTrigger>
      <TooltipContent>{t(`home.exportStatusTooltips.${tooltipKey}`)}</TooltipContent>
    </Tooltip>
  );
};
